import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAuth } from '@/auth/useAuth';
import { useMembersStore } from '@/store/membersStore';
import { Button, EmptyState, Screen } from '../ui';

/**
 * Accepting an invite. Signed out, the token is remembered through sign-in rather than
 * lost — an invite link that drops you on a sign-in page and forgets why is a dead end.
 */
export default function Invite() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    setBusy(true);
    void useMembersStore
      .getState()
      .accept(token)
      .then(({ appId }) => navigate(`/app/${appId}`, { replace: true }))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'That invite could not be accepted.'),
      )
      .finally(() => setBusy(false));
  }, [isAuthenticated, token, navigate]);

  if (!isAuthenticated) {
    return (
      <Screen title="You've been invited">
        <EmptyState
          icon="account"
          title="Sign in to accept"
          body="Create an account or sign in, and we'll add you to the app."
          ctaTitle="Sign in"
          onCta={() => navigate('/sign-in', { state: { from: `/invite/${token}` } })}
        />
      </Screen>
    );
  }

  return (
    <Screen title="Joining…">
      {busy && <p role="status" className="type-body">Accepting your invite…</p>}
      {error && (
        <>
          <p role="alert" className="type-caption field-error-text">{error}</p>
          <Button title="Go home" variant="secondary" onPress={() => navigate('/')} />
        </>
      )}
    </Screen>
  );
}
